import { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useRestaurant } from '@core/tenant/TenantProvider';
import { useBooking } from '@features/booking/state';
import { AcceptCheckbox } from './AcceptCheckbox';
import { TermsModal } from './TermsModal';
import { formatAmount } from '@shared/utils/formatAmount';

interface PaymentAcceptancesProps {
  guaranteeType: 'deposit' | 'no_show' | null;
  feeAmount: number;
  displayCurrency: string;
  serviceFee: number;
  cancellationHours?: number;
  isWaitingList?: boolean;
  onAllAcceptedChange: (allAccepted: boolean) => void;
}

export function PaymentAcceptances({
  guaranteeType,
  feeAmount,
  displayCurrency,
  serviceFee,
  cancellationHours,
  isWaitingList = false,
  onAllAcceptedChange,
}: PaymentAcceptancesProps) {
  const { t, i18n } = useTranslation('booking');
  const restaurant = useRestaurant();
  const booking = useBooking();

  // Replicate legacy: check if cancellation window has already passed
  const canCancelOrChange = useMemo(() => {
    if (!cancellationHours || cancellationHours <= 0) return false;
    if (!booking.selectedDate || !booking.selectedTime) return false;
    const datePart = booking.selectedDate.slice(0, 10); // YYYY-MM-DD from ISO
    const reservationDate = new Date(`${datePart}T${booking.selectedTime}:00`);
    const deadlineDate = new Date(Date.now() + cancellationHours * 60 * 60 * 1000);
    return deadlineDate.getTime() < reservationDate.getTime();
  }, [cancellationHours, booking.selectedDate, booking.selectedTime]);

  const [accepted1, setAccepted1] = useState(false);
  const [accepted2, setAccepted2] = useState(false);
  const [accepted3, setAccepted3] = useState(false);
  const [accepted4, setAccepted4] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);

  // Acceptance text changes with guaranteeType / feeAmount. Reset checkboxes
  // when terms change so the user has to re-acknowledge what they're accepting.
  useEffect(() => {
    setAccepted1(false);
    setAccepted2(false);
    setAccepted3(false);
    setAccepted4(false);
    onAllAcceptedChange(false);
  }, [guaranteeType, feeAmount, onAllAcceptedChange]);

  /** Look up text from restaurant.appTexts[lang], falling back to i18n */
  const getAppText = (
    appTextKey: string,
    fallbackKey: string,
    fallbackDefault?: string,
    replacements?: Record<string, string>,
  ): string => {
    const lang = i18n.language || 'en';
    const texts = restaurant.appTexts?.[lang];
    let text: string;
    if (texts && texts[appTextKey]) {
      text = texts[appTextKey];
    } else {
      text = t(fallbackKey, { ...replacements, defaultValue: fallbackDefault || '' });
    }
    if (replacements) {
      for (const [key, value] of Object.entries(replacements)) {
        text = text.replace(new RegExp(`\\{\\{?${key}\\}\\}?`, 'g'), value);
      }
    }
    return text;
  };

  const toggle = (
    setter: React.Dispatch<React.SetStateAction<boolean>>,
    current: boolean,
  ) => {
    const newVal = !current;
    setter(newVal);
    const next = {
      a1: setter === setAccepted1 ? newVal : accepted1,
      a2: setter === setAccepted2 ? newVal : accepted2,
      a3: setter === setAccepted3 ? newVal : accepted3,
      a4: setter === setAccepted4 ? newVal : accepted4,
    };
    onAllAcceptedChange(next.a1 && next.a2 && next.a3 && next.a4);
  };

  let cancellationText: string;
  if (cancellationHours == null) {
    cancellationText = getAppText(
      'NEW_MODAL_CONDITION_CANCEL',
      'PAYMENT_CANCELLATION_POLICY',
    );
  } else if (cancellationHours < 0 || !canCancelOrChange) {
    // Negative hours or deadline already passed → can't cancel
    cancellationText = getAppText(
      'CANT_CHANGE_RESERVATION',
      'CANT_CHANGE_RESERVATION',
      'Cancellation or changes are not possible for this reservation',
    );
  } else {
    const hoursStr = String(cancellationHours);
    cancellationText = getAppText(
      'NEW_MODAL_CONDITION_CANCEL',
      'NEW_MODAL_CONDITION_CANCEL',
      'Cancel or change requires at least {{hours}} hours notice',
      { hours: hoursStr, cancellationHours: hoursStr },
    );
  }

  const termsLabel = getAppText(
    'NEW_MODAL_ACCEPT_TERMS_AND_CONDITIONS',
    'TERMS_ACCEPT_CHECKBOX',
  );

  // Collect custom terms paragraphs from appTexts (MODAL_TERMS_TEXT_1..10)
  const lang = i18n.language || 'en';
  const appTextsLang = restaurant.appTexts?.[lang];
  const termsTexts: string[] = [];
  if (appTextsLang) {
    for (let i = 1; i <= 10; i++) {
      const text = appTextsLang[`MODAL_TERMS_TEXT_${i}`];
      if (text) termsTexts.push(text);
    }
  }

  const termsCheckbox = (
    <AcceptCheckbox
      accepted={accepted4}
      onToggle={() => toggle(setAccepted4, accepted4)}
      label={termsLabel}
      linkLabel={t('TERMS_ACCEPT_READ')}
      onLinkClick={() => setShowTermsModal(true)}
    />
  );

  const termsModal = (
    <TermsModal
      open={showTermsModal}
      onClose={() => setShowTermsModal(false)}
      cancellationHours={cancellationHours}
      termsTexts={termsTexts}
      contactEmail={restaurant.contactEmail}
      phone={restaurant.phone}
    />
  );

  // WL flow: matches legacy personalize-step-2.tpl.html conditions.
  // Uses legacy keys so restaurant appTexts overrides apply correctly.
  // deposit WL: NOT_EXTRA_COST (or ADDITIONAL_PROCESSING_FEE) + WL condition + T&C
  // no_show WL: WL condition + NO_CHARGE + T&C
  if (isWaitingList) {
    const wlCondition = guaranteeType === 'deposit'
      ? getAppText(
          'NEW_MODAL_CONDITION_DEPOSIT_WAITING_LIST',
          'NEW_MODAL_CONDITION_DEPOSIT_WAITING_LIST',
          undefined,
          { currency: displayCurrency, amount: formatAmount(feeAmount, i18n.language) },
        )
      : getAppText(
          'NEW_MODAL_CONDITION_NO_SHOW_WAITING_LIST',
          'NEW_MODAL_CONDITION_NO_SHOW_WAITING_LIST',
          undefined,
          { currency: displayCurrency, amount: formatAmount(feeAmount, i18n.language) },
        );

    // deposit: show cost label (same as normal deposit flow)
    // no_show: show no-charge label
    const extraLabel = guaranteeType === 'deposit'
      ? (serviceFee > 0
          ? getAppText('ADDITIONAL_PROCESSING_FEE', 'ADDITIONAL_PROCESSING_FEE')
          : getAppText('NOT_EXTRA_COST', 'NOT_EXTRA_COST'))
      : getAppText('NEW_MODAL_CONDITION_NO_CHARGE', 'NEW_MODAL_CONDITION_NO_CHARGE');

    const handleWlToggle = (
      setter: React.Dispatch<React.SetStateAction<boolean>>,
      current: boolean,
    ) => {
      const newVal = !current;
      setter(newVal);
      const next = {
        a1: setter === setAccepted1 ? newVal : accepted1,
        a2: setter === setAccepted2 ? newVal : accepted2,
        a4: setter === setAccepted4 ? newVal : accepted4,
      };
      onAllAcceptedChange(next.a1 && next.a2 && next.a4);
    };

    return (
      <div className="space-y-5">
        <AcceptCheckbox
          accepted={accepted1}
          onToggle={() => handleWlToggle(setAccepted1, accepted1)}
          label={wlCondition}
        />
        <AcceptCheckbox
          accepted={accepted2}
          onToggle={() => handleWlToggle(setAccepted2, accepted2)}
          label={extraLabel}
        />
        <AcceptCheckbox
          accepted={accepted4}
          onToggle={() => handleWlToggle(setAccepted4, accepted4)}
          label={termsLabel}
          linkLabel={t('TERMS_ACCEPT_READ')}
          onLinkClick={() => setShowTermsModal(true)}
        />
        {termsModal}
      </div>
    );
  }

  if (guaranteeType === 'deposit') {
    const depositTitle = getAppText(
      'RESERVATION_REQUIRE_DEPOSIT_TITLE',
      'RESERVATION_REQUIRE_DEPOSIT_TITLE',
      'A deposit will be charged',
    );
    const depositAmount = t('RESERVATION_REQUIRE_DEPOSIT_LOCAL_CURRENCY', {
      currency: displayCurrency,
      amount: formatAmount(feeAmount, i18n.language),
      defaultValue: 'Amount to be charged: {{currency}} {{amount}}',
    });

    const costLabel = serviceFee > 0
      ? getAppText(
          'ADDITIONAL_PROCESSING_FEE',
          'ADDITIONAL_PROCESSING_FEE',
          'An additional processing fee applies',
        )
      : getAppText(
          'NOT_EXTRA_COST',
          'NOT_EXTRA_COST',
          'This will be credited to your final bill',
        );

    return (
      <div className="space-y-5">
        <AcceptCheckbox
          accepted={accepted1}
          onToggle={() => toggle(setAccepted1, accepted1)}
          label={depositTitle}
          sublabel={depositAmount}
        />
        <AcceptCheckbox
          accepted={accepted2}
          onToggle={() => toggle(setAccepted2, accepted2)}
          label={costLabel}
        />
        <AcceptCheckbox
          accepted={accepted3}
          onToggle={() => toggle(setAccepted3, accepted3)}
          label={cancellationText}
        />
        {termsCheckbox}
        {termsModal}
      </div>
    );
  }

  // No-show (default)
  const noShowTitle = getAppText(
    'RESERVATION_REQUIRE_NO_SHOW_TITLE',
    'RESERVATION_REQUIRE_NO_SHOW_TITLE',
  );
  const noShowAmount = t('RESERVATION_REQUIRE_NO_SHOW_LOCAL_CURRENCY', {
    currency: displayCurrency,
    amount: formatAmount(feeAmount, i18n.language),
  });
  const noChargeLabel = getAppText(
    'NEW_MODAL_CONDITION_NO_CHARGE',
    'NEW_MODAL_CONDITION_NO_CHARGE',
  );

  return (
    <div className="space-y-5">
      <AcceptCheckbox
        accepted={accepted1}
        onToggle={() => toggle(setAccepted1, accepted1)}
        label={cancellationText}
      />
      <AcceptCheckbox
        accepted={accepted2}
        onToggle={() => toggle(setAccepted2, accepted2)}
        label={noShowTitle}
        sublabel={noShowAmount}
      />
      <AcceptCheckbox
        accepted={accepted3}
        onToggle={() => toggle(setAccepted3, accepted3)}
        label={noChargeLabel}
      />
      {termsCheckbox}
      {termsModal}
    </div>
  );
}
